
import { useState, useEffect, useCallback } from "react"
import { api } from "@/lib/api"

export type ExpiryDateFormat = 'DD/MM/YYYY' | 'MM/YYYY'

const STORAGE_KEY = 'expiryDateFormat'
const FORMAT_CHANGED_EVENT = 'expiryDateFormatChanged'
const DEFAULT_FORMAT: ExpiryDateFormat = 'DD/MM/YYYY'

function isValidFormat(value: unknown): value is ExpiryDateFormat {
  return value === 'DD/MM/YYYY' || value === 'MM/YYYY'
}

/**
 * Fetch expiry date format from backend and cache to localStorage
 */
export async function fetchExpiryDateFormat(): Promise<ExpiryDateFormat> {
  try {
    const result = await api.get<{ format: string }>('/organizations/settings/expiry-date-format')
    const format = result.format
    if (isValidFormat(format)) {
      localStorage.setItem(STORAGE_KEY, format)
      window.dispatchEvent(new Event(FORMAT_CHANGED_EVENT))
      return format
    }
  } catch {
    // Fallback to localStorage if API fails
  }
  return getCachedExpiryDateFormat()
}

/**
 * Get cached expiry date format from localStorage (synchronous)
 */
function getCachedExpiryDateFormat(): ExpiryDateFormat {
  if (typeof window === 'undefined') return DEFAULT_FORMAT
  const saved = localStorage.getItem(STORAGE_KEY)
  if (isValidFormat(saved)) {
    return saved
  }
  return DEFAULT_FORMAT
}

/**
 * Hook to manage expiry date format setting
 * Returns current format and utility functions for formatting/parsing dates
 */
export function useExpiryDateFormat() {
  const [format, setFormat] = useState<ExpiryDateFormat>(getCachedExpiryDateFormat)

  // Correct SSR hydration mismatch + listen for same-tab and cross-tab changes
  useEffect(() => {
    // Re-read from localStorage on client mount (SSR may have used default)
    setFormat(getCachedExpiryDateFormat())

    // Same-tab changes (from saveExpiryDateFormat / fetchExpiryDateFormat)
    const handleFormatChanged = () => {
      setFormat(getCachedExpiryDateFormat())
    }
    // Cross-tab changes (StorageEvent only fires in other tabs)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) {
        handleFormatChanged()
      }
    }
    window.addEventListener(FORMAT_CHANGED_EVENT, handleFormatChanged)
    window.addEventListener('storage', handleStorageChange)
    return () => {
      window.removeEventListener(FORMAT_CHANGED_EVENT, handleFormatChanged)
      window.removeEventListener('storage', handleStorageChange)
    }
  }, [])

  /**
   * Format a date string or Date object for display
   * @param date - ISO date string (YYYY-MM-DD) or Date object
   * @returns Formatted string based on current format setting (numeric with slashes)
   */
  const formatExpiry = useCallback((date: string | Date | null | undefined): string => {
    if (!date) return format === 'MM/YYYY' ? 'MM / YYYY' : 'DD / MM / YYYY'

    try {
      const d = typeof date === 'string' ? new Date(date) : date
      if (isNaN(d.getTime())) return format === 'MM/YYYY' ? 'MM / YYYY' : 'DD / MM / YYYY'

      const month = String(d.getMonth() + 1).padStart(2, '0')
      const year = d.getFullYear()

      if (format === 'MM/YYYY') {
        // Month/Year format: 09 / 2029
        return `${month} / ${year}`
      } else {
        // Day/Month/Year format: 15 / 09 / 2029
        const day = String(d.getDate()).padStart(2, '0')
        return `${day} / ${month} / ${year}`
      }
    } catch {
      return format === 'MM/YYYY' ? 'MM / YYYY' : 'DD / MM / YYYY'
    }
  }, [format])

  /**
   * Format a date for display in a compact form (for tables, lists)
   * @param date - ISO date string (YYYY-MM-DD) or Date object
   * @returns Compact formatted string (numeric with slashes)
   */
  const formatExpiryCompact = useCallback((date: string | Date | null | undefined): string => {
    if (!date) return format === 'MM/YYYY' ? 'MM / YYYY' : 'DD / MM / YYYY'

    try {
      const d = typeof date === 'string' ? new Date(date) : date
      if (isNaN(d.getTime())) return format === 'MM/YYYY' ? 'MM / YYYY' : 'DD / MM / YYYY'

      const month = String(d.getMonth() + 1).padStart(2, '0')
      const year = d.getFullYear()

      if (format === 'MM/YYYY') {
        // Month/Year format: 09 / 2029
        return `${month} / ${year}`
      } else {
        // Day/Month/Year format: 15 / 09 / 2029
        const day = String(d.getDate()).padStart(2, '0')
        return `${day} / ${month} / ${year}`
      }
    } catch {
      return format === 'MM/YYYY' ? 'MM / YYYY' : 'DD / MM / YYYY'
    }
  }, [format])

  /**
   * Get placeholder text for date input based on format
   */
  const getPlaceholder = useCallback((): string => {
    return format === 'MM/YYYY' ? 'MM / YYYY' : 'DD / MM / YYYY'
  }, [format])

  /**
   * Get input type for date fields
   * For MM/YYYY format, we use month input; for DD/MM/YYYY, we use date input
   */
  const getInputType = useCallback((): 'date' | 'month' => {
    return format === 'MM/YYYY' ? 'month' : 'date'
  }, [format])

  /**
   * Convert a date value to the appropriate input format
   * @param date - ISO date string (YYYY-MM-DD)
   * @returns Value for input field
   */
  const toInputValue = useCallback((date: string | null | undefined): string => {
    if (!date) return ''

    if (format === 'MM/YYYY') {
      // For month input, need YYYY-MM format
      return date.substring(0, 7) // YYYY-MM
    }
    return date // YYYY-MM-DD for date input
  }, [format])

  /**
   * Convert input value to ISO date string for storage
   * @param value - Value from input field
   * @returns ISO date string (YYYY-MM-DD)
   */
  const fromInputValue = useCallback((value: string): string => {
    if (!value) return ''

    if (format === 'MM/YYYY') {
      // Month input gives YYYY-MM, append last day of month
      const [y, m] = value.split('-').map(Number)
      const lastDay = new Date(y, m, 0).getDate()
      return `${value}-${String(lastDay).padStart(2, '0')}`
    }
    return value // Date input gives YYYY-MM-DD
  }, [format])

  return {
    format,
    formatExpiry,
    formatExpiryCompact,
    getPlaceholder,
    getInputType,
    toInputValue,
    fromInputValue,
    isMonthOnly: format === 'MM/YYYY'
  }
}

/**
 * Get expiry date format synchronously (for non-React contexts)
 * Uses localStorage cache for immediate access
 */
export function getExpiryDateFormat(): ExpiryDateFormat {
  return getCachedExpiryDateFormat()
}

/**
 * Format a date string according to an expiry date format setting.
 * Returns compact format suitable for labels (no spaces around slashes).
 */
export function formatDateByFormat(date: string | Date | null | undefined, format: string): string {
  if (!date) return ''
  try {
    const d = typeof date === 'string' ? new Date(date) : date
    if (isNaN(d.getTime())) return ''

    const day = String(d.getUTCDate()).padStart(2, '0')
    const month = String(d.getUTCMonth() + 1).padStart(2, '0')
    const year = d.getUTCFullYear()

    switch (format) {
      case 'MM/YYYY': return `${month}/${year}`
      case 'YYYY-MM-DD': return `${year}-${month}-${day}`
      case 'MM/DD/YYYY': return `${month}/${day}/${year}`
      case 'DD/MM/YYYY':
      default: return `${day}/${month}/${year}`
    }
  } catch {
    return ''
  }
}

/**
 * Save expiry date format to backend and update localStorage cache
 */
export async function saveExpiryDateFormat(format: ExpiryDateFormat): Promise<void> {
  localStorage.setItem(STORAGE_KEY, format)
  window.dispatchEvent(new Event(FORMAT_CHANGED_EVENT))
  try {
    await api.put('/organizations/settings/expiry-date-format', { format })
  } catch {
    // localStorage is already updated, backend will sync on next successful call
  }
}
